import { useDeleteFilesMutation } from '@gen2/api/files/hooks';
import { TInviteRequestFile } from '@gen2/api/invite-requests/api';
import { useConfirmationModalStore } from '@gen2/app/components/confirmation-modal/hooks/confirmation-modal-store';
import { useFileStore } from '@gen2/app/components/invite-request/hooks/file-store';
import { SetupPlugin } from '@gen2/app/components/rich-text-editor/plugins/SetupPlugin/SetupPlugin';
import RichTextEditor from '@gen2/app/components/rich-text-editor/rich-text-editor';
import { useFileUpload } from '@gen2/app/hooks/use-file-upload';
import { TInviteRequestsForm } from '@gen2/app/invites/send-invites/requests/requests';
import { MAX_MESSAGE_LENGTH } from '@gen2/app/invites/send-invites/requests/schema';
import { useAuth, useToast } from '@gen2/hooks';
import { $generateHtmlFromNodes } from '@lexical/html';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { Alert, FormHelperText, Stack } from '@mui/material';
import {
  allowedFileTypes,
  DragAndDrop,
  FileUpload,
  formatSize,
  renderIcon,
} from '@nx-fe/components';
import { $getRoot, EditorState, LexicalEditor } from 'lexical';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TRequestContainer, useRequestHook } from '../../hooks/request';
import { RequestContacts } from '../../request-contact/request-contact';
import {
  StyledAttachments,
  StyledAttachmentsLabel,
  StyledFileUploadRequest,
} from './file-upload.styled';
import { MAX_FILE_SIZE_IN_MB } from './schema';

export type TFileUploadRequestForm = {
  message: string;
  meta: string;
};

export type TFileUploadRequestProps = {
  id: number;
  container?: TRequestContainer;
};

export const FileUploadRequest = React.memo(
  ({ id, container = 'invite' }: TFileUploadRequestProps) => {
    const {
      setValue,
      formState: { errors },
    } = useFormContext<TInviteRequestsForm>();
    const fileTypes = allowedFileTypes();
    const fileSize = MAX_FILE_SIZE_IN_MB / 1024 / 1024;

    const { setIsConfirmationModalOpen } = useConfirmationModalStore();
    const { t } = useTranslation('sendInvite');
    const { onUpdate, req } = useRequestHook({ id, container });
    const { setFile } = useFileStore();
    const toast = useToast();
    const [fileErrors, setFileErrors] = useState<string[]>();
    const [isUploadLoading, setIsUploadLoading] = useState(false);
    const { isLoading: isFileDeleting } = useDeleteFilesMutation();
    const { featureFlags } = useAuth();
    const attachments = useMemo(() => {
      if (!req.files) return [];

      return req.files.filter((file) => file.type === 'send_invite_attachment');
    }, [req.files]);

    const { handleFileUpload } = useFileUpload({
      container,
      requestId: req?.id,
      existingFilesCount: attachments.length,
      onSettled: () => setIsUploadLoading(false),
    });

    const onMessageChange = (
      editorState: EditorState,
      editor: LexicalEditor,
    ) => {
      editorState.read(() => {
        const root = $getRoot();

        const rawText = root.getTextContent();
        const rawHTML = $generateHtmlFromNodes(editor);

        // if no text set back to empty
        if (rawText.length === 0) {
          setValue(`requests.${id}.description`, '');
          setValue(`requests.${id}.meta`, '');
        } else {
          setValue(`requests.${id}.description`, rawHTML);
          setValue(`requests.${id}.descriptionText`, rawText);
          setValue(`requests.${id}.meta`, JSON.stringify(editorState.toJSON()));
        }
      });
    };

    const onEditorSetup = (editor: LexicalEditor) => {
      const editorState = editor.getEditorState();

      editorState.read(() => {
        const rawHTML = $generateHtmlFromNodes(editor);

        // Only set description if it's not empty
        if (
          rawHTML === '<p><br></p>' ||
          rawHTML === '<p></p>' ||
          rawHTML === '<br>' ||
          !rawHTML.trim()
        ) {
          setValue(`requests.${id}.description`, '');
        } else {
          setValue(`requests.${id}.description`, rawHTML);
        }
      });
    };

    const onFileUpload = async (files: File[]) => {
      setIsUploadLoading(true);
      setFileErrors([]);

      const errors = await handleFileUpload(files);

      if (errors) {
        setIsUploadLoading(false);

        if (!errors.message.includes('max')) {
          toast.show({
            text: t(errors.message, { ns: 'sendInvite', fileTypes, fileSize }),
            variant: 'error',
          });
        }
      }
    };

    const contacts = useMemo(() => {
      if (req.contacts) return req.contacts;

      return [];
    }, [req]);

    const clearError = (errorMessage: string) => {
      setFileErrors((prev) => prev?.filter((err) => err !== errorMessage));
    };

    const handleDelete = (file: TInviteRequestFile) => {
      if (isFileDeleting) return;

      setFile({
        ...file,
        request: req,
      });

      setIsConfirmationModalOpen(true);
    };

    const placeholder = useMemo(() => {
      return req.type === 'read-only'
        ? t('request.readOnly.message.placeholder')
        : t('request.fileUpload.message.placeholder');
    }, [req.type]);

    return (
      <StyledFileUploadRequest key={req.id}>
        {(container === 'invite' || container === 'follow-up') && (
          <RequestContacts request={req} contacts={contacts} />
        )}

        <div>
          <RichTextEditor onBlur={onUpdate} placeholder={placeholder}>
            <OnChangePlugin onChange={onMessageChange} />
            <SetupPlugin
              onSetup={onEditorSetup}
              maxLength={MAX_MESSAGE_LENGTH}
              defaultEditorState={req?.meta}
              defaultHtmlState={req?.description}
            />
          </RichTextEditor>
          {(errors?.requests && errors?.requests[id])?.descriptionText
            ?.message && (
            <FormHelperText error>
              {t(errors.requests[id]?.descriptionText?.message ?? '')}
            </FormHelperText>
          )}
        </div>
        {!!attachments.length && (
          <div>
            <StyledAttachmentsLabel>
              <span>Attachments</span>
              <span data-cy="number-of-file">{attachments.length} file(s)</span>
            </StyledAttachmentsLabel>
            <StyledAttachments>
              {req.files
                .filter((file) => file.type === 'send_invite_attachment')
                .map((file) => (
                  <FileUpload
                    key={file.id}
                    progress={100}
                    title={file.original_name}
                    fileSize={`(${formatSize(file.size)})`}
                    isUploading={false}
                    icon={renderIcon(file.mime_type)}
                    isDeleting={isFileDeleting}
                    onDelete={() => handleDelete(file)}
                    data-cy={`file-${file.id}`}
                  />
                ))}
            </StyledAttachments>
          </div>
        )}
        <Stack rowGap={2}>
          {!!fileErrors?.length &&
            fileErrors.map((err) => (
              <Alert
                data-cy="file-upload-error"
                key={err}
                severity="error"
                onClose={() => clearError(err)}
              >
                {t(`${err}`, { fileTypes, fileSize })}
              </Alert>
            ))}
          <DragAndDrop
            isLoading={isUploadLoading}
            onUpload={onFileUpload}
            accept=".png,.jpg,.jpeg,.pdf,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.heic,.heif"
          />
        </Stack>
      </StyledFileUploadRequest>
    );
  },
  (prevProps, nextProps) => {
    return _.isEqual(prevProps, nextProps);
  },
);
